// src/utils/subscriptionUtils.js
import API_BASE_URL from '../config';

export const checkSubscriptionLimits = (employeeCount, requestedImportCount = 0) => {
  // Get subscription from sessionStorage
  const subscription = JSON.parse(sessionStorage.getItem('subscription') || '{}');
  const FREE_TIER_LIMIT = 2; // Free tier limit
  const totalCount = employeeCount + requestedImportCount;

  console.log('📊 Subscription check:', {
    employeeCount,
    requestedImportCount,
    totalCount,
    subscription,
    isPaid: subscription?.isPaid,
    plan: subscription?.plan,
    planName: subscription?.planName
  });

  // If user has paid subscription or pro/enterprise plan, allow unlimited
  const isPaidUser = subscription?.isPaid || 
                     subscription?.plan?.toLowerCase().includes('pro') || 
                     subscription?.plan?.toLowerCase().includes('enterprise') ||
                     subscription?.plan?.toLowerCase().includes('premium') ||
                     subscription?.planName?.toLowerCase().includes('pro') ||
                     subscription?.planName?.toLowerCase().includes('enterprise') ||
                     subscription?.planName?.toLowerCase().includes('premium') ||
                     subscription?.status === 'active' ||
                     subscription?.subscription_status === 'active';

  if (isPaidUser) {
    return { 
      allowed: true, 
      reason: 'Pro/Enterprise plan: Unlimited employees',
      currentCount: employeeCount,
      requestedImportCount,
      totalCount,
      limit: 'Unlimited',
      isPaid: true,
      canBulkImport: true
    };
  }
  
  // Check for trial period
  const isTrialActive = subscription?.isTrialActive || 
                       (subscription?.trialEndDate && new Date(subscription.trialEndDate) > new Date()) ||
                       (subscription?.remainingTrialDays && subscription.remainingTrialDays > 0);

  if (isTrialActive) {
    const remainingDays = subscription.remainingTrialDays || 
                         Math.ceil((new Date(subscription.trialEndDate) - new Date()) / (1000 * 60 * 60 * 24));
    
    if (employeeCount >= FREE_TIER_LIMIT) {
      return { 
        allowed: false, 
        reason: `Trial period limit reached. You can onboard up to ${FREE_TIER_LIMIT} employees during trial. Trial ends in ${remainingDays} days.`,
        currentCount: employeeCount,
        requestedImportCount,
        totalCount,
        limit: FREE_TIER_LIMIT,
        isPaid: false,
        canBulkImport: false,
        trialActive: true,
        remainingTrialDays: remainingDays
      };
    }
    
    return { 
      allowed: true, 
      reason: `Trial period: ${employeeCount}/${FREE_TIER_LIMIT} employees used. Trial ends in ${remainingDays} days.`,
      currentCount: employeeCount,
      requestedImportCount,
      totalCount,
      limit: FREE_TIER_LIMIT,
      isPaid: false,
      canBulkImport: false,
      trialActive: true,
      remainingTrialDays: remainingDays
    };
  }
  
  // Check if trial has ended
  const trialEnded = subscription?.trialEnded || 
                    (subscription?.trialEndDate && new Date(subscription.trialEndDate) <= new Date());

  if (trialEnded) {
    return { 
      allowed: false, 
      reason: `Your free trial has ended. Upgrade to Pro/Enterprise plan to continue using the platform and onboard more employees.`,
      currentCount: employeeCount,
      requestedImportCount,
      totalCount,
      limit: FREE_TIER_LIMIT,
      isPaid: false,
      canBulkImport: false,
      trialEnded: true
    };
  }
  
  // Free tier (no trial) - strict limit of 2
  if (employeeCount >= FREE_TIER_LIMIT) {
    return { 
      allowed: false, 
      reason: `Free tier is limited to ${FREE_TIER_LIMIT} employees. Upgrade to Pro/Enterprise plan for unlimited employees.`,
      currentCount: employeeCount,
      requestedImportCount,
      totalCount,
      limit: FREE_TIER_LIMIT,
      isPaid: false,
      canBulkImport: false
    };
  }

  // Check if requested import would exceed limit
  if (totalCount > FREE_TIER_LIMIT) {
    return { 
      allowed: false, 
      reason: `Importing ${requestedImportCount} employees would exceed the free tier limit of ${FREE_TIER_LIMIT}. You currently have ${employeeCount} employees.`,
      currentCount: employeeCount,
      requestedImportCount,
      totalCount,
      limit: FREE_TIER_LIMIT,
      isPaid: false,
      canBulkImport: false,
      wouldExceedLimit: true
    };
  }
  
  return { 
    allowed: true, 
    reason: `Free tier: ${employeeCount}/${FREE_TIER_LIMIT} employees used`,
    currentCount: employeeCount,
    requestedImportCount,
    totalCount,
    limit: FREE_TIER_LIMIT,
    isPaid: false,
    canBulkImport: false
  };
};

export const getSubscriptionInfo = () => {
  const subscription = JSON.parse(sessionStorage.getItem('subscription') || '{}');
  console.log('📋 Current subscription info:', subscription);
  return subscription;
};

export const isPaidSubscription = () => {
  const subscription = getSubscriptionInfo();
  return subscription?.isPaid || 
         subscription?.plan?.toLowerCase().includes('pro') || 
         subscription?.plan?.toLowerCase().includes('enterprise') ||
         subscription?.plan?.toLowerCase().includes('premium') ||
         subscription?.planName?.toLowerCase().includes('pro') ||
         subscription?.planName?.toLowerCase().includes('enterprise') ||
         subscription?.planName?.toLowerCase().includes('premium') ||
         subscription?.status === 'active' ||
         subscription?.subscription_status === 'active';
};

export const canBulkImport = () => {
  const subscription = getSubscriptionInfo();
  return subscription?.isPaid || 
         subscription?.plan?.toLowerCase().includes('pro') || 
         subscription?.plan?.toLowerCase().includes('enterprise') ||
         subscription?.plan?.toLowerCase().includes('premium') ||
         subscription?.planName?.toLowerCase().includes('pro') ||
         subscription?.planName?.toLowerCase().includes('enterprise') ||
         subscription?.planName?.toLowerCase().includes('premium');
};

export const refreshSubscription = async () => {
  try {
    const token = sessionStorage.getItem('token');
    const userEmail = sessionStorage.getItem('userEmail');
    
    if (!token || !userEmail) {
      console.log('❌ No token or email found');
      return null;
    }

    console.log('🔄 Refreshing subscription status...');
    
    const response = await fetch(
      `${API_BASE_URL}/api/hr/subscription-status?userEmail=${encodeURIComponent(userEmail)}`, 
      {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.ok) {
      const data = await response.json();
      console.log('✅ Subscription refreshed:', data);
      
      if (data.success && data.data) {
        const subscriptionData = data.data;
        
        // Normalize the plan name
        let planName = 'Free Tier';
        let isPaid = false;
        
        if (subscriptionData.planName) {
          planName = subscriptionData.planName;
          const planLower = planName.toLowerCase();
          isPaid = planLower.includes('pro') || planLower.includes('enterprise') || planLower.includes('premium');
        } else if (subscriptionData.plan) {
          const planLower = subscriptionData.plan.toLowerCase();
          if (planLower.includes('pro')) {
            planName = 'Pro Plan';
            isPaid = true;
          } else if (planLower.includes('enterprise')) {
            planName = 'Enterprise Plan';
            isPaid = true;
          } else if (planLower.includes('premium')) {
            planName = 'Premium Plan';
            isPaid = true;
          } else if (planLower.includes('basic')) {
            planName = 'Basic Plan';
          } else if (planLower.includes('trial')) {
            planName = 'Trial Plan';
          } else {
            planName = subscriptionData.plan.charAt(0).toUpperCase() + subscriptionData.plan.slice(1) + ' Plan';
            isPaid = planLower.includes('pro') || planLower.includes('enterprise') || planLower.includes('premium');
          }
        }

        const normalizedSubscription = {
          ...subscriptionData,
          planName: planName,
          plan: subscriptionData.plan || 'Free Tier',
          isPaid: isPaid || subscriptionData.isPaid || subscriptionData.status === 'active',
          isTrialActive: subscriptionData.isTrialActive || 
                        (subscriptionData.trialEndDate && new Date(subscriptionData.trialEndDate) > new Date()),
          trialEnded: subscriptionData.trialEnded || 
                     (subscriptionData.trialEndDate && new Date(subscriptionData.trialEndDate) <= new Date()),
          remainingTrialDays: subscriptionData.remainingTrialDays || 
                            (subscriptionData.trialEndDate 
                              ? Math.ceil((new Date(subscriptionData.trialEndDate) - new Date()) / (1000 * 60 * 60 * 24))
                              : 0),
          remainingSubscriptionDays: subscriptionData.remainingSubscriptionDays || 0,
          canBulkImport: isPaid
        };

        console.log('✅ Normalized subscription saved:', normalizedSubscription);
        sessionStorage.setItem('subscription', JSON.stringify(normalizedSubscription));
        return normalizedSubscription;
      }
    }
    return null;
  } catch (error) {
    console.error('❌ Error refreshing subscription:', error);
    return null;
  }
};

// Helper function to check if bulk import is allowed
export const checkBulkImportAllowed = (employeeCount, importCount) => {
  const result = checkSubscriptionLimits(employeeCount, importCount);
  
  if (!result.allowed) {
    return {
      allowed: false,
      message: result.reason,
      details: result
    };
  }
  
  if (!result.isPaid) {
    return {
      allowed: false,
      message: `Bulk import is only available for Pro/Enterprise plans. Free tier is limited to individual employee onboarding.`,
      details: result
    };
  }
  
  return {
    allowed: true,
    message: 'Bulk import is allowed',
    details: result
  };
};